import type { Competence } from '@/models/interfaces/Competence'
import { apiClient } from '@/plugins/axios/api'
import { SousDomaineService } from '@/services/sousDomaine/SousDomaineService'

/** Competence relation to sous-domaine may come back as fkSousdomaineId or the legacy fkSousDomaineId. */
function competenceSousDomaineId(c: Competence): number {
  return c.fkSousdomaineId ?? c.fkSousDomaineId ?? 0
}

export class CompetenceService {
  static async getAll(): Promise<Competence[]> {
    try {
      const response = await apiClient.get('/api/competences')
      return response.data['hydra:member'] || []
    } catch {
      console.debug('Competence endpoint not available yet, returning empty array.')
      return []
    }
  }

  /**
   * The Competence API no longer exposes fkDomaineId (checked against /api/docs.jsonld): a
   * competence relates to its domaine only transitively, through fkSousdomaineId -> Sousdomaine.fkDomaineId.
   * So this resolves the domaine's sous-domaines first, then filters competences client-side.
   */
  static async getByDomaineId(domaineId: number): Promise<Competence[]> {
    try {
      const [competences, sousDomaines] = await Promise.all([
        CompetenceService.getAll(),
        SousDomaineService.getAll(),
      ])
      const sousDomaineIds = new Set(
        sousDomaines.filter((s) => s.fkDomaineId === domaineId).map((s) => s.sousDomaineId),
      )
      return competences.filter((c) => sousDomaineIds.has(competenceSousDomaineId(c)))
    } catch {
      console.debug('Competence endpoint not available yet, returning empty array.')
      return []
    }
  }
}
